import styled, {css} from 'styled-components';

import {IconButtonUniqueId} from 'components/ui-kit/IconButton';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {Spinner} from 'components/ui-kit/Spinner';
import {Tag} from 'components/ui-kit/Tags';
import {Textarea} from 'components/ui-kit/Textarea';
import {border, colors, font, breakpoint} from 'theme';

export const ErrorMessage = styled.div`
    padding: 32px;
    color: ${colors.danger};
    text-align: center;
`;

export const DetailCard = styled(ContentContainer)`
    width: 100%;
    box-sizing: border-box;
`;

export const DetailTitle = styled.h1`
    margin: 0;
    color: ${colors.darkGrey};
    font-size: 18px;
    line-height: 24px;
`;

export const ActionBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid ${colors.border};

    > div {
        display: flex;
        align-items: center;
        gap: 12px;
    }
`;

export const Section = styled.section`
    padding: 20px 0;
    border-bottom: 1px solid ${colors.border};

    &:last-child {
        border-bottom: 0;
    }
`;

export const FilesSection = styled(Section)`
    > h2 {
        font-weight: 600;
        letter-spacing: 0.52px;
    }
`;

export const SectionTitle = styled.h2`
    margin: 0 0 16px;
    color: ${colors.brandPrimary};
    font-size: 13px;
    line-height: 18px;
    text-transform: uppercase;
`;

export const DataGrid = styled.div<{borderBottom?: boolean}>`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px 24px;
    border-bottom: ${({borderBottom}) =>
        borderBottom
            ? `
        1px solid ${colors.border};
        padding-bottom: 16px;
        margin-bottom: 16px;
    `
            : ''};

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

export const Field = styled.div`
    min-width: 0;
`;

export const FieldLabel = styled.div`
    margin-bottom: 4px;
    color: ${colors.textSecondary};
    font-family: ${font.family.primary};
    font-size: 12px;
    line-height: 16px;
`;

export const FieldValue = styled.div`
    color: ${colors.darkGrey};
    font-size: 14px;
    line-height: 20px;
    overflow-wrap: anywhere;
`;

export const Assignment = styled.div`
    display: grid;
    grid-template-columns: minmax(180px, 1fr);
    align-items: end;
    gap: 12px;
    padding: 20px 0 0;

    button {
        width: auto;
    }

    @media (max-width: 1024px) {
        grid-template-columns: minmax(180px, 1fr) auto;
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

export const AssignmentsWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;

    @media (max-width: ${breakpoint.tablet}) {
        flex-direction: column;
    }
`;

export const EmptyText = styled.div`
    margin-top: 12px;
    color: ${colors.danger};
    font-size: 13px;
    font-weight: 600;
`;

export const MarkingCodeActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 16px;

    button {
        width: auto;
    }
`;

export const FileList = styled.ul`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin: 0;
    padding: 0;
    list-style: none;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

export const FileRow = styled.li`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    box-sizing: border-box;
    width: 100%;
    padding: 12px 16px;
    border-radius: ${border.radius};
    background: #f8f8f8;
`;

export const FileName = styled.span`
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    color: ${colors.darkGrey};
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const FileDownloadButton = styled(IconButtonUniqueId)`
    box-sizing: border-box;
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 10px;
    border-radius: ${border.radius};
    background-color: ${colors.brandLight};
    color: ${colors.brandPrimary};

    &:not(:disabled):hover,
    &:not(:disabled):focus-visible {
        background-color: ${colors.brandPrimary};
        color: ${colors.white};

        svg {
            stroke: ${colors.white};
        }
    }
`;

export const FileDownloadSpinner = styled(Spinner)`
    box-sizing: border-box;
    display: block;
    flex-shrink: 0;
    width: 16px;
    min-width: 16px;
    height: 16px;
    min-height: 16px;
`;

export const ResponseSection = styled(Section)`
    > h2 {
        font-weight: 600;
        letter-spacing: 0.52px;
    }
`;

export const ResponseGrid = styled.div<{$withUpload?: boolean}>`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    align-items: stretch;
    width: 100%;

    ${({$withUpload}) =>
        $withUpload &&
        css`
            textarea {
                min-height: 154px;
            }
        `}

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

export const ResponseTextarea = styled(Textarea)`
    height: 100%;

    textarea {
        height: 100%;
        min-height: 150px;
        padding: 16px;
        border-color: ${colors.textMuted};
        color: ${colors.darkGrey};
        font-size: 14px;
        line-height: 18px;

        &::-webkit-input-placeholder {
            color: ${colors.textSecondary};
        }

        &::-moz-placeholder {
            color: ${colors.textSecondary};
        }

        &:-ms-input-placeholder {
            color: ${colors.textSecondary};
        }

        &::-ms-input-placeholder {
            color: ${colors.textSecondary};
        }
    }
`;

export const ResponseDropzone = styled.div<{isDragActive: boolean}>`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    min-width: 0;
    min-height: 154px;
    padding: 32px;
    border: 2px dashed ${({isDragActive}) => (isDragActive ? colors.brandPrimary : '#cad5e2')};
    border-radius: 14px;
    background-color: ${({isDragActive}) => (isDragActive ? colors.brandLight : colors.white)};
    cursor: pointer;
    outline: none;
    transition: border-color 200ms ease, background-color 200ms ease;

    &:hover,
    &:focus-visible {
        border-color: ${colors.brandPrimary};
        background-color: ${colors.brandLight};
    }
`;

export const ResponseDropzoneText = styled.p`
    margin: 0;
    color: #0a0a0a;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    text-align: center;
`;

export const ResponseDropzoneAction = styled.span`
    color: ${colors.brandPrimary};
`;

export const ResponseDropzoneHint = styled.span`
    color: #62748e;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    text-align: center;
`;

export const ResponseFiles = styled.div`
    margin-top: 16px;
`;

export const HistorySection = styled(Section)`
    > h2 {
        margin-bottom: 20px;
        font-weight: 700;
        letter-spacing: 0.52px;
    }
`;

export const HistoryList = styled.ol`
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: 0;
    list-style: none;
`;

export const HistoryItem = styled.li`
    display: flex;
    align-items: stretch;
    width: 100%;
`;

export const HistoryRail = styled.div`
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    align-items: center;
    width: 30px;
    margin-right: 18px;
`;

export const HistoryDot = styled.span`
    flex-shrink: 0;
    width: 10px;
    height: 10px;
    margin-top: 21px;
    border-radius: 50%;
    background: ${colors.brandPrimary};
`;

export const HistoryLine = styled.span`
    flex: 1 1 auto;
    width: 1px;
    min-height: 12px;
    margin-top: 5px;
    background: #e8ebee;

    ${HistoryItem}:last-child & {
        visibility: hidden;
    }
`;

export const HistoryContent = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
    padding: 12px 0 22px;
`;

export const HistoryMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
`;

export const HistoryAction = styled.span<{$color: string}>`
    padding: 5px 8px;
    border-radius: 5px;
    color: ${({$color}) => $color};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.6px;
    line-height: 10px;
    text-transform: uppercase;
`;

export const HistoryMetaText = styled.span`
    color: ${colors.textSecondary};
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0.1px;
    line-height: 16px;
`;

export const HistoryMetaDot = styled.span`
    color: ${colors.textMuted};
    font-size: 12px;
    line-height: 16px;
`;

export const StatusChange = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
`;

export const HistoryStatusBadge = styled(Tag)`
    margin: 0;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    line-height: 13px;
`;

export const HistoryChevron = styled.span`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
`;

export const HistoryPayload = styled.div`
    margin-top: 10px;
    color: ${colors.textSecondary};
    font-size: 12px;
    line-height: 16px;
    overflow-wrap: anywhere;
`;
